import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserRound, Save, ArrowRight, Sparkles, Sun, Moon, Monitor } from "lucide-react";
import { maskPhone } from "@/lib/format";
import { Checkbox } from "@/components/ui/checkbox";
import FirmSeats from "@/components/FirmSeats";
import { BusinessSearch } from "@/components/BusinessSearch";
import { applyTheme, saveThemeToAccount, saveLangToAccount } from "@/lib/theme";
import { useI18n } from "@/context/I18nContext";
import { LANGS } from "@/lib/translations";
import { useSosEnabled } from "@/lib/siteConfig";

const THEME_OPTIONS = [
  { key: "light", label: "Light", icon: Sun },
  { key: "dark", label: "Dark", icon: Moon },
  { key: "auto", label: "Auto", icon: Monitor },
];

function AppearanceSetting() {
  const [pref, setPref] = useState(() => localStorage.getItem("nc_theme_pref") || (localStorage.getItem("nc_theme") === "light" ? "light" : "dark"));
  useEffect(() => {
    const sync = () => setPref(localStorage.getItem("nc_theme_pref") || (localStorage.getItem("nc_theme") === "light" ? "light" : "dark"));
    window.addEventListener("nc-theme-changed", sync);
    return () => window.removeEventListener("nc-theme-changed", sync);
  }, []);
  const pick = (key) => { setPref(key); applyTheme(key); saveThemeToAccount(key); };
  return (
    <div className="border-t border-border pt-8 mt-2" data-testid="appearance-settings">
      <h2 className="font-serif text-2xl mb-1">Appearance</h2>
      <p className="text-sm text-muted-foreground mb-4">Choose your color scheme — "Auto" follows your device's light/dark setting. Saved to your account.</p>
      <div className="inline-flex border-2 border-border rounded-sm overflow-hidden" role="group">
        {THEME_OPTIONS.map((o) => {
          const active = pref === o.key;
          return (
            <button key={o.key} onClick={() => pick(o.key)} data-testid={`theme-opt-${o.key}`}
              className={`flex items-center gap-2 px-5 h-11 text-sm font-semibold transition-colors ${active ? "bg-accent text-accent-foreground" : "bg-background text-muted-foreground hover:bg-secondary"}`}>
              <o.icon size={16} /> {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LanguageSetting() {
  const { lang, setLang } = useI18n();
  const pick = (code) => { setLang(code); saveLangToAccount(code); };
  return (
    <div className="mt-8" data-testid="language-settings">
      <h2 className="font-serif text-2xl mb-1">Language</h2>
      <p className="text-sm text-muted-foreground mb-4">Set your preferred language for the site. Saved to your account.</p>
      <div className="flex flex-wrap gap-2">
        {LANGS.map((l) => {
          const active = lang === l.code;
          return (
            <button key={l.code} onClick={() => pick(l.code)} data-testid={`lang-opt-${l.code}`}
              className={`h-11 px-5 border-2 text-sm font-semibold transition-colors ${active ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:bg-secondary"}`}>
              {l.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const GROUPS = [
  {
    title: "Your details", fields: [
      { key: "full_name", label: "Full name", placeholder: "Jane M. Doe" },
      { key: "county", label: "NC County", placeholder: "Wake" },
      { key: "case_number", label: "Default case / file number", placeholder: "24-CVD-009988" },
    ],
  },
  {
    title: "Contact", fields: [
      { key: "address_line", label: "Street address", placeholder: "123 Main St, Apt 4" },
      { key: "city", label: "City", placeholder: "Raleigh" },
      { key: "state", label: "State", placeholder: "North Carolina" },
      { key: "zip_code", label: "ZIP code", placeholder: "27601" },
      { key: "phone", label: "Phone", placeholder: "(919) 555-0132" },
      { key: "email", label: "Email", placeholder: "you@example.com" },
    ],
  },
  {
    title: "Attorney / firm (optional)", fields: [
      { key: "attorney_name", label: "Attorney name", placeholder: "John Q. Counsel" },
      { key: "bar_number", label: "NC State Bar number", placeholder: "12345" },
      { key: "firm", label: "Firm / law office", placeholder: "Acme Law LLC" },
      { key: "suite", label: "Suite / unit number", placeholder: "Suite 200" },
    ],
  },
  {
    title: "Business filing (optional)", fields: [
      { key: "entity_name", label: "Business / entity name", placeholder: "Acme Holdings LLC" },
      { key: "registered_agent", label: "Registered agent", placeholder: "Jane M. Doe" },
    ],
  },
];

export default function Profile() {
  const siteSosOn = useSosEnabled();
  const [form, setForm] = useState({ state: "North Carolina" });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/profile").then(({ data }) => setForm((f) => ({ ...f, ...data }))).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const barDigits = String(form.bar_number || "").replace(/\D/g, "");
  const barInvalid = !!form.is_attorney && !!form.bar_number && (barDigits.length < 1 || barDigits.length > 7);

  const save = async () => {
    if (barInvalid) { toast.error("Bar number must be 1–7 digits (numbers only)."); return; }
    setBusy(true);
    try {
      await api.put("/profile", form);
      window.dispatchEvent(new CustomEvent("nc-sos-pref-changed", { detail: !!form.hide_sos }));
      toast.success("Your info is saved — use “Use My Info” on any form to autofill it");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save your info");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Navbar />
      <section className="max-w-3xl mx-auto px-6 pt-12 pb-24 min-h-[60vh]" data-testid="profile-page">
        <p className="overline text-accent mb-2">Your Account</p>
        <h1 className="font-serif text-5xl tracking-tighter mb-3">My info</h1>
        <p className="text-muted-foreground mb-8 max-w-xl flex items-start gap-2">
          <Sparkles size={16} className="text-accent mt-1 shrink-0" />
          Save the details you type on every court form once. Then hit <span className="font-medium text-foreground">“Use My Info”</span> in any form editor to autofill matching fields instantly.
        </p>

        {loading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-10">
            {GROUPS.map((g) => (
              <div key={g.title}>
                <p className="overline text-accent mb-4 pb-2 border-b border-border">{g.title}</p>
                {g.title.startsWith("Attorney") && (
                  <label className="flex items-start gap-3 mb-4 p-3 border-2 border-black cursor-pointer" data-testid="is-attorney-row">
                    <Checkbox checked={!!form.is_attorney} onCheckedChange={(c) => set("is_attorney", !!c)} data-testid="is-attorney-checkbox" className="mt-0.5" />
                    <span className="text-sm">
                      <span className="font-semibold">I’m an attorney</span>
                      <span className="block text-muted-foreground text-xs mt-0.5">Auto-fill my State Bar number on any form that has a bar-number field.</span>
                    </span>
                  </label>
                )}
                {g.title.startsWith("Business filing") && (
                  <div className="mb-4 p-4 border-2 border-black/10 bg-secondary/40" data-testid="profile-business-lookup">
                    <p className="text-sm font-semibold mb-1">Look up my business</p>
                    <p className="text-xs text-muted-foreground mb-3">Search the official Secretary of State records to attach your entity — we’ll fill in the name and registered agent below.</p>
                    <BusinessSearch defaultQuery={form.entity_name || ""} defaultState={form.state === "North Carolina" ? "NC" : "NC"}
                      onSelect={(biz) => {
                        set("entity_name", biz.entity_name);
                        set("registered_agent", biz.registered_agent);
                        toast.success(`${biz.entity_name} selected — click “Save my info” to keep it.`);
                      }} />
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  {g.fields.map((fl) => (
                    <div key={fl.key} className={fl.key === "address_line" ? "sm:col-span-2" : ""}>
                      <Label className="text-xs">{fl.label}</Label>
                      <Input data-testid={`profile-${fl.key}`} value={fl.key === "phone" ? maskPhone(form[fl.key] || "") : (form[fl.key] || "")} placeholder={fl.placeholder}
                        aria-invalid={fl.key === "bar_number" && barInvalid}
                        onChange={(e) => set(fl.key, fl.key === "phone" ? maskPhone(e.target.value) : e.target.value)}
                        className={`rounded-sm h-11 mt-1 ${fl.key === "bar_number" && barInvalid ? "border-accent" : ""}`} />
                      {fl.key === "bar_number" && (
                        <div className="flex items-center justify-between mt-1">
                          <span className={`text-xs ${barInvalid ? "text-accent" : "text-muted-foreground"}`} data-testid="bar-hint">
                            {barInvalid ? "Enter 1–7 digits (numbers only)." : "NC bar numbers are numeric."}
                          </span>
                          <a href="https://portal.ncbar.gov/verification/search.aspx" target="_blank" rel="noopener noreferrer"
                            data-testid="bar-verify-link" className="text-xs text-accent hover:underline">Verify on NC State Bar ↗</a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <FirmSeats />

            {siteSosOn && <div data-testid="preferences-section">
              <p className="overline text-accent mb-4 pb-2 border-b border-border">Preferences</p>
              <label className="flex items-start gap-3 p-3 border-2 border-black cursor-pointer" data-testid="hide-sos-row">
                <Checkbox checked={!!form.hide_sos} onCheckedChange={(c) => set("hide_sos", !!c)} data-testid="hide-sos-checkbox" className="mt-0.5" />
                <span className="text-sm">
                  <span className="font-semibold">Hide NC Secretary of State forms</span>
                  <span className="block text-muted-foreground text-xs mt-0.5">If you only need court forms, turn this on to remove the “NC SOS Forms” section from your navigation and homepage.</span>
                </span>
              </label>
            </div>}

            <div className="flex flex-wrap gap-3 pt-2">
              <Button onClick={save} disabled={busy} data-testid="profile-save-btn"
                className="rounded-sm gap-2 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
                <Save size={16} /> {busy ? "Saving…" : "Save my info"}
              </Button>
              <Link to="/forms"><Button variant="outline" className="rounded-sm gap-2 hover:bg-secondary">Browse forms <ArrowRight size={16} /></Button></Link>
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-2">
              <UserRound size={13} /> Your info is stored privately to your account and only used to autofill forms you open.
            </p>
          </div>
        )}
        <AppearanceSetting />
        <LanguageSetting />
      </section>
      <Footer />
    </div>
  );
}
